import { useCallback, useEffect, useRef, useState } from 'react'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { ErrorScreen } from '@/components/feedback/ErrorScreen'
import { LoadingScreen } from '@/components/feedback/LoadingScreen'
import { PickerScreen } from '@/components/feedback/PickerScreen'
import { Builder } from '@/components/layout/Builder'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  AUTH0_SCOPE,
} from '@/lib/auth'
import { getDevBypassToken } from '@/lib/devBypass'
import { getRegion } from '@/lib/env'
import { fetchAvailableSubscriptions } from '@/lib/subscriptionsApi'
import { ACTIVE_SUB_STORAGE_KEY, type Subscription } from '@/lib/subscription'

const LOGIN_ATTEMPT_KEY = 'n3o:link-builder:login-attempt:v1'

export type GetToken = () => Promise<string | null>

export default function App() {
  const region = getRegion()
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/${region}/`,
        audience: AUTH0_AUDIENCE,
        scope: AUTH0_SCOPE,
      }}
      cacheLocation="memory"
    >
      <Shell>
        <AuthenticatedRoot />
      </Shell>
    </Auth0Provider>
  )
}

export function BypassApp({ bypass }: { bypass: Subscription }) {
  const getToken = useCallback<GetToken>(() => Promise.resolve(getDevBypassToken()), [])
  return (
    <Shell>
      <ActiveSubscriptionGate subscriptions={[bypass]} getToken={getToken} />
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={200}>
      {children}
      <Toaster position="top-center" />
    </TooltipProvider>
  )
}

function AuthenticatedRoot() {
  const { isAuthenticated, isLoading, user, error, loginWithRedirect, getAccessTokenSilently } =
    useAuth0()

  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({
        id: user.sub,
        email: user.email,
        username: user.nickname,
      })
    }
  }, [isAuthenticated, user])

  // Ref'd because Auth0 returns a new function identity per render.
  const getTokenRef = useRef(getAccessTokenSilently)
  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently
  }, [getAccessTokenSilently])

  const getToken = useCallback<GetToken>(async () => (await getTokenRef.current()) ?? null, [])

  // sessionStorage sentinel guards against a redirect loop when Auth0 sends
  // us back unauthenticated with no error (silent-auth failure).
  const [loginFailed, setLoginFailed] = useState(false)
  useEffect(() => {
    if (isLoading || isAuthenticated || error) return
    if (sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) {
      sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot derived-from-sessionStorage signal
      setLoginFailed(true)
      return
    }
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1')
    loginWithRedirect()
  }, [isLoading, isAuthenticated, error, loginWithRedirect])

  useEffect(() => {
    if (isAuthenticated) sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
  }, [isAuthenticated])

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; subscriptions: Subscription[] }
    | { status: 'error' }
  >({ status: 'loading' })

  useEffect(() => {
    if (!isAuthenticated) return
    const controller = new AbortController()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset to loading when auth changes; alternative would be stale data flash
    setState({ status: 'loading' })
    ;(async () => {
      try {
        const token = await getToken()
        if (controller.signal.aborted) return
        if (token === null) {
          setState({ status: 'error' })
          return
        }
        const subscriptions = await fetchAvailableSubscriptions({
          token,
          signal: controller.signal,
        })
        if (controller.signal.aborted) return
        setState({ status: 'ready', subscriptions })
      } catch (err: unknown) {
        if (controller.signal.aborted) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        setState({ status: 'error' })
      }
    })()
    return () => controller.abort()
  }, [isAuthenticated, getToken])

  if (isLoading || (!isAuthenticated && !error && !loginFailed)) {
    return <LoadingScreen label="Signing you in" />
  }
  if (error || loginFailed) {
    return (
      <ErrorScreen reason="fetch-failed" onRetry={() => window.location.reload()} />
    )
  }
  if (state.status === 'loading') return <LoadingScreen label="Loading subscriptions" />
  if (state.status === 'error') {
    return (
      <ErrorScreen reason="fetch-failed" onRetry={() => window.location.reload()} />
    )
  }
  if (state.subscriptions.length === 0) {
    return (
      <ErrorScreen reason="no-subscription" onRetry={() => window.location.reload()} />
    )
  }
  return <ActiveSubscriptionGate subscriptions={state.subscriptions} getToken={getToken} />
}

interface ActiveSubscriptionGateProps {
  subscriptions: Subscription[]
  getToken: GetToken
}

function ActiveSubscriptionGate({ subscriptions, getToken }: ActiveSubscriptionGateProps) {
  const [activeId, setActiveId] = useState<string | null>(() => {
    const stored = localStorage.getItem(ACTIVE_SUB_STORAGE_KEY)
    if (stored && subscriptions.some((s) => s.id === stored)) return stored
    if (subscriptions.length === 1) return subscriptions[0]?.id ?? null
    return null
  })

  useEffect(() => {
    if (activeId !== null) {
      localStorage.setItem(ACTIVE_SUB_STORAGE_KEY, activeId)
    } else {
      localStorage.removeItem(ACTIVE_SUB_STORAGE_KEY)
    }
  }, [activeId])

  const handlePick = (sub: Subscription) => setActiveId(sub.id)

  const active = subscriptions.find((s) => s.id === activeId)

  useEffect(() => {
    if (active && active.code !== '0') {
      Sentry.setTag('subscriptionCode', active.code)
    }
  }, [active])
  if (!active) {
    return <PickerScreen subscriptions={subscriptions} onPick={handlePick} />
  }

  // key={active.code} → fresh mount per subscription (avoids save/hydrate races).
  return (
    <Builder
      key={active.code}
      active={active}
      available={subscriptions}
      onPickSubscription={handlePick}
      getToken={getToken}
    />
  )
}